import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../hooks/useAuth";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Reached via PrivateRoute's redirect when user.mustResetPassword is true
// (set by a fleet admin's PUT /users/{id}/password reset) -- current
// password is whatever the admin just gave the user out of band. Unlike
// ResetPassword.js (unauthenticated, token-based, for "forgot password"),
// this always runs against an already-logged-in session.
const ChangePassword = () => {
  const { user, clearMustResetPassword, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("current_password", currentPassword);
      body.append("new_password", newPassword);
      await axios.post(`${API_BASE}/change_password`, body, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      clearMustResetPassword();
      navigate("/dispatch", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't change your password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-white px-4 py-12">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200">
        <h2 className="mb-1 text-center text-2xl font-bold text-gray-800">Set a new password</h2>
        <p className="mb-6 text-center text-sm text-gray-500">
          An admin reset your password. Enter it below along with the new password you'd like to use.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="rounded-md bg-red-50 px-4 py-2 text-sm text-red-600 border border-red-200">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
              Current (temporary) password
            </label>
            <input
              id="current-password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition disabled:opacity-50"
          >
            {loading ? "Saving…" : "Set new password"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          <button
            type="button"
            onClick={logout}
            className="font-medium text-blue-600 hover:text-blue-800"
          >
            Sign out instead
          </button>
        </p>
      </div>
    </div>
  );
};

export default ChangePassword;
